/**
 * Safety Page - Community and Safety Standards
 * =============================================
 * Public page - accessible without authentication
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Heart, Users, AlertTriangle, Flag, Ban, Eye, Phone, Mail, CheckCircle } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const SafetyPage = () => {
  const navigate = useNavigate();

  const safetyTips = [
    {
      icon: Users,
      title: 'Meeting In Person',
      tips: [
        'Always meet in a public place for the first few dates',
        'Tell a friend or family member your plans',
        'Arrange your own transportation',
        'Stay sober and alert',
        'Trust your instincts—if something feels off, leave'
      ]
    },
    {
      icon: Shield,
      title: 'Protecting Your Information',
      tips: [
        'Never share financial information or send money',
        'Keep personal details private until you know someone',
        'Use NearU messaging until you\'re comfortable',
        'Be cautious of requests for personal photos',
        'Report anyone asking for inappropriate information'
      ]
    },
    {
      icon: Eye,
      title: 'Spotting Red Flags',
      tips: [
        'Requests for money or financial help',
        'Refusing to video chat or meet in person',
        'Inconsistent stories or information',
        'Pressuring you to move off the platform',
        'Aggressive or controlling behavior'
      ]
    }
  ];

  const communityStandards = [
    {
      icon: Heart,
      title: 'Respect Everyone',
      description: 'Treat all members with dignity and respect. Harassment, hate speech, and discrimination have no place on NearU.'
    },
    {
      icon: CheckCircle,
      title: 'Be Authentic',
      description: 'Use your real name and recent photos. Misrepresenting yourself undermines trust in our community.'
    },
    {
      icon: Shield,
      title: 'Keep It Safe',
      description: 'Never share illegal content, threaten violence, or engage in any activity that could harm others.'
    },
    {
      icon: Ban,
      title: 'No Spam or Scams',
      description: 'Commercial solicitation, phishing, and scam attempts result in immediate account termination.'
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)',
      paddingBottom: '40px'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'rgba(26, 15, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px 20px',
        borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <NearULogo size="small" showText={true} />
      </div>

      <div style={{ padding: '24px 20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '28px',
          fontWeight: '600',
          color: '#E6D7C3',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          Community & Safety
        </h1>
        
        <p style={{
          color: '#A57C33',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          Your safety is our priority
        </p>

        {/* Safety Commitment Banner */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.15) 0%, rgba(165, 124, 51, 0.08) 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          border: '1px solid rgba(215, 181, 109, 0.3)',
          textAlign: 'center'
        }}>
          <Shield size={48} color="#D7B56D" style={{ marginBottom: '16px' }} />
          <h2 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '20px',
            color: '#E6D7C3',
            marginBottom: '12px'
          }}>
            Our Commitment to You
          </h2>
          <p style={{
            color: '#A57C33',
            fontSize: '15px',
            lineHeight: '1.7',
            margin: 0
          }}>
            NearU is committed to creating a safe, respectful community for all members. 
            We actively monitor for suspicious activity, respond quickly to reports, 
            and continuously improve our safety features to protect you.
          </p>
        </div>

        {/* Community Standards */}
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '22px',
          color: '#E6D7C3',
          marginBottom: '20px'
        }}>
          Community Standards
        </h2>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '16px',
          marginBottom: '40px'
        }}>
          {communityStandards.map((standard, index) => {
            const Icon = standard.icon;
            return (
              <div
                key={index}
                style={{
                  background: 'rgba(26, 15, 10, 0.6)',
                  borderRadius: '16px',
                  padding: '20px',
                  border: '1px solid rgba(165, 124, 51, 0.15)'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <Icon size={24} color="#D7B56D" />
                </div>
                <h3 style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '16px',
                  color: '#E6D7C3',
                  marginBottom: '8px'
                }}>
                  {standard.title}
                </h3>
                <p style={{
                  color: '#A57C33',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {standard.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Safety Tips */}
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '22px',
          color: '#E6D7C3',
          marginBottom: '20px'
        }}>
          Safety Tips
        </h2>

        {safetyTips.map((section, index) => {
          const Icon = section.icon;
          return (
            <div
              key={index}
              style={{
                background: 'rgba(26, 15, 10, 0.6)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '16px',
                border: '1px solid rgba(165, 124, 51, 0.15)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={20} color="#D7B56D" />
                </div>
                <h3 style={{
                  fontFamily: 'Cinzel, serif',
                  fontSize: '18px',
                  color: '#E6D7C3',
                  margin: 0
                }}>
                  {section.title}
                </h3>
              </div>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '20px',
                listStyleType: 'none'
              }}>
                {section.tips.map((tip, tipIndex) => (
                  <li 
                    key={tipIndex}
                    style={{
                      color: '#A57C33',
                      fontSize: '14px',
                      lineHeight: '1.8',
                      position: 'relative',
                      paddingLeft: '20px',
                      marginBottom: '8px'
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: '#D7B56D'
                    }}>•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {/* Report Section */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(183, 28, 28, 0.05) 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '32px',
          border: '1px solid rgba(244, 67, 54, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <Flag size={28} color="#F44336" />
            <h3 style={{
              fontFamily: 'Cinzel, serif',
              fontSize: '20px',
              color: '#E6D7C3',
              margin: 0
            }}>
              Report a Concern
            </h3>
          </div>
          <p style={{
            color: '#A57C33',
            fontSize: '14px',
            lineHeight: '1.7',
            marginBottom: '20px'
          }}>
            If you encounter behavior that violates our community standards or makes you feel unsafe, 
            please report it immediately. You can report users directly from their profile, 
            or contact our Trust & Safety team.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a
              href="mailto:safety@nearu.com"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'rgba(244, 67, 54, 0.2)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
                borderRadius: '10px',
                color: '#F44336',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              <Mail size={18} />
              safety@nearu.com
            </a>
          </div>
        </div>

        {/* Emergency Contact */}
        <div style={{
          background: 'rgba(26, 15, 10, 0.6)',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '16px',
          border: '1px solid rgba(165, 124, 51, 0.15)',
          textAlign: 'center'
        }}>
          <AlertTriangle size={32} color="#FFC107" style={{ marginBottom: '12px' }} />
          <h3 style={{
            fontFamily: 'Cinzel, serif',
            fontSize: '18px',
            color: '#E6D7C3',
            marginBottom: '8px'
          }}>
            In Case of Emergency
          </h3>
          <p style={{
            color: '#A57C33',
            fontSize: '14px',
            lineHeight: '1.6',
            marginBottom: '16px'
          }}>
            If you are in immediate danger, please contact your local emergency services.
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
            borderRadius: '10px',
            color: '#1A0F0A',
            fontSize: '16px',
            fontWeight: '700'
          }}>
            <Phone size={20} />
            Call 911
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyPage;
